import { publishSourceIdentityChange, SOURCE_IDENTITY_CHANGED } from './sourceIdentity';
export { SOURCE_IDENTITY_CHANGED } from './sourceIdentity';
/** One recovery per reader identity, shared by previews, visuals and chat. No credentials live here. */
export type SourceAuthIssue = {
    state: 'sign_in_required' | 'setup_required' | 'unavailable' | 'access_denied';
    identityKey?: string; connectionId?: number; reportId?: string; authType?: string;
    serverType?: string; serverName?: string; databaseName?: string; canReconnect?: boolean;
};
export type Recovery = SourceAuthIssue & { key: string; message: string; readKeys?: string[] };
const issues = new Map<string, Recovery>();
const waiters = new Map<string, Set<(ok: boolean) => void>>();
const notices = new Set<string>();
const listeners = new Set<() => void>();
let snapshot: Recovery[] = [];
const emit = () => { snapshot = Array.from(issues.values()); listeners.forEach(fn => fn()); };
export const subscribeSourceRecovery = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
export const sourceRecoveries = () => snapshot;

export function sourceIssue(payload: any): SourceAuthIssue | undefined {
    const issue = payload?.code?.startsWith?.('source_auth_') && payload?.sourceAuth;
    return issue && ['sign_in_required', 'setup_required', 'unavailable', 'access_denied'].includes(issue.state) ? issue : undefined;
}

/** HTML reports return independent dataset outcomes within one successful envelope. */
export function sourceAuthPayload(payload: any): any | undefined {
    if (sourceIssue(payload)) return payload;
    if (payload?.results && typeof payload.results === 'object') return Object.values(payload.results).find(sourceIssue);
    return undefined;
}

export function reportSourceIssue(payload: any, reportId?: string, notice = true): Recovery | undefined {
    const issue = sourceIssue(payload);
    if (!issue?.connectionId) return undefined; // Unsaved connection forms own their own sign-in controls.
    const key = issue.identityKey || `connection:${issue.connectionId}`;
    if (notice || !issue.canReconnect) notices.add(key);
    const entry = { ...issues.get(key), ...issue, reportId: reportId || issue.reportId, key, message: payload.message || payload.error };
    issues.set(key, entry); emit();
    return entry;
}

export function finishSourceRecovery(key: string, ok: boolean): void {
    issues.delete(key);
    notices.delete(key);
    const pending = waiters.get(key); waiters.delete(key);
    pending?.forEach(finish => finish(ok)); emit();
}

export function clearSourceRecoveries(): void {
    new Set([...Array.from(issues.keys()), ...Array.from(waiters.keys())]).forEach(key => finishSourceRecovery(key, false));
}

export function sourceIdentityChanged(identityKey?: string, connected = true): void {
    publishSourceIdentityChange({ identityKey, connected });
}

window.addEventListener(SOURCE_IDENTITY_CHANGED, event => {
    const { identityKey, connected } = (event as CustomEvent).detail || {};
    if (identityKey) finishSourceRecovery(identityKey, connected);
});

export function recoverSourceAuth(payload: any, options: { reportId?: string; signal?: AbortSignal; readKey?: string } = {}): Promise<boolean> {
    if (options.signal?.aborted) return Promise.resolve(false);
    const entry = reportSourceIssue(payload, options.reportId, false);
    if (entry && options.readKey) { entry.readKeys = Array.from(new Set([...(entry.readKeys || []), options.readKey.toLowerCase()])); emit(); }
    if (!entry?.canReconnect || options.signal?.aborted) return Promise.resolve(false);
    return new Promise(resolve => {
        const finish = (ok: boolean) => {
            clearTimeout(timer); options.signal?.removeEventListener('abort', cancel);
            const pending = waiters.get(entry.key);
            pending?.delete(finish);
            if (pending && !pending.size) {
                waiters.delete(entry.key);
                if (!notices.has(entry.key)) { issues.delete(entry.key); emit(); }
            }
            resolve(ok);
        };
        const cancel = () => finish(false);
        const timer = setTimeout(cancel, 10 * 60 * 1000);
        options.signal?.addEventListener('abort', cancel, { once: true });
        const pending = waiters.get(entry.key) || new Set(); pending.add(finish); waiters.set(entry.key, pending);
    });
}

/** Explicit read endpoints only. Never replay connection saves, visual edits or chat turns. */
export function sourceReadRequest(method: string, path: string): boolean {
    if (path.includes('/browser-auth/') || path.includes('/analysis-services/auth/') || path.startsWith('/api/read-jobs/')) return false;
    if (method.toUpperCase() === 'GET') return true;
    return method.toUpperCase() === 'POST' && (
        path === '/api/sql/execute' || path === '/get-schema' || path === '/api/get-schema' ||
        /^\/api\/reports\/[^/]+\/data-model\/sources\/\d+\/entities\/preview$/.test(path) ||
        /^\/api\/reports\/[^/]+\/data-model\/query$/.test(path) ||
        /^\/api\/connections\/analysis-services\/\d+\/query$/.test(path));
}

export function requestReportId(url: string, data?: any): string | undefined {
    try {
        const uri = new URL(url, window.location.origin);
        const body = typeof data === 'string' ? JSON.parse(data) : data;
        return body?.reportId || uri.searchParams.get('reportId') || uri.pathname.match(/\/reports\/([^/]+)\//)?.[1];
    } catch { return undefined; }
}
